import React, { useState, useEffect, Fragment } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Grid,
  Paper,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  Typography,
  MenuItem,
  Backdrop,
  CircularProgress,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import RefreshIcon from "@material-ui/icons/Refresh";
import AddIcon from "@material-ui/icons/Add";
import CheckIcon from "@material-ui/icons/Check";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import InfoIcon from "@material-ui/icons/Info";
import notify from "../utils/notifier";
import AdminDashboardService from "../services/AdminDashboardService";
import { makeStyles } from "@material-ui/core/styles";
import theme from "../themes/lightTheme";

/**
 * Boolean options for dropdown selection
 */
const BOOLEAN_OPTIONS = [
  { value: "true", label: "True" },
  { value: "false", label: "False" },
];

/**
 * Table column configuration for regenerated secrets display
 */
const RESPONSE_COLUMNS = [
  { id: "property", label: "Property", width: "30%" },
  { id: "value", label: "Value", width: "70%" },
];

/**
 * Feature descriptions for tooltips
 * Provides detailed explanations for each client configuration field
 */
const FEATURE_DESCRIPTIONS = {
    auditRequest: "audit request - stores the audit request setting to control saving of sensitive data in the database.",
    blacklistedDomains: "blacklisted domains - controls domain validation to block disallowed or blacklisted email domains.",
    fromEmails: "from emails - stores verified sender email addresses used by the client.",
    emailsRecipientsValidation: "emails recipients validation - enables recipient email validation to check for anomalies.",
    emailQuotaData: "email quota data - stores the client's daily email sending limit.",
    callbackDetails: "callback details - sends callback details to the client — full parameters on first mapping, and only changes (delta) on template updates.",
    enablePushBasedEvents: "enable push based events - enables sending of delivery events to the client via azure service bus.",
    enableTrackingEvents: "enable tracking events - enables tracking of delivery events pushed to the client.",
    enableDnsCheck: "enable dns check - enables dns verification for sender domains.",
    enableParameterValidation: "enable parameter validation - enables validation of request parameters based on defined templates.",
    enableEmailNotification: "enable email notification - sends an email notification on client creation if enabled.",
    notificationEmailAddresses: "notification email addresses - stores client email addresses for receiving system notifications.",
    isActiveStatus: "is active status - indicates whether the client is active and allowed to send notifications.",
    mockNotificationApiEnabled: "mock notification api enabled - enables mock responses instead of calling actual service providers.",
    otpConfiguration: "otp configuration - stores otp-related configuration such as code length, retry count, and expiry.",
    rateLimiterData: "rate limiter data - stores api rate-limiting configurations for the client.",
    smsQuotaData: "sms quota data - stores the client's daily sms sending limit.",
    docxToPdfStatergy: "docx to pdf strategy - sets the strategy for converting docx to pdf using gotenberg, aspose, or adobe converter.",
    enableOverrideDocxToPDFStatergyAtClientLevel: "enable override docx to pdf strategy at client level - if true, uses the strategy mapped to the template in template_lists; if false, enforces the strategy at the client level.",
    mergePdfStatergy: "merge pdf strategy - defines the method used to merge multiple pdf files (description not provided).",
    asposeConversionStrategy: "aspose conversion strategy - defines settings specific to Aspose document conversion (description not provided).",
    region: "region - stores the client's region for regional routing or configuration purposes (description not provided).",
    encryptionStrategy: "encryption strategy - determines how encryption is applied to stored or transmitted data (description not provided).",
    imageToPdfConversionStatergy: "image to pdf conversion strategy - defines strategy for image to pdf conversion using either aspose api or pdfkit node package.",
    enablFileNameValidationForUpload: "enable file name validation for upload - validates file name for illegal characters and ensures fallback to uploaded file name if output name is missing.",
    readExpiryDurationForPresignedDownloadUrl: "read expiry duration for presigned download url - sets how long a presigned download link remains valid (description not provided).",
    clientsWithTemplateAccess: "clients with template access - maintains list of clients allowed to access specific templates (description not provided).",
    pdfCompressionStatergy: "pdf compression strategy - defines the strategy used to compress pdf files (description not provided).",
    fileStorageSettings: "file storage settings - holds settings for storing files, e.g., bucket config, folder path, etc. (description not provided).",
    fileTypeValidation: "file type validation - validates the uploaded file's mime type; can be enabled at both global and client levels.",
    isFileValidationEnabled: "is file validation enabled - performs a malicious content check for uploaded pdfs.",
    splitPdfStatergy: "split pdf strategy - defines how a PDF should be split into parts (description not provided).",
    enableFileExtensionValidation: "enable file extension validation - enables validation of the file extension during upload."
  };
  

/**
 * Determines the field type based on the field name and value
 * 
 * @param {string} fieldName - The name of the field
 * @param {any} value - The current value of the field
 * @param {Object} clientSettings - The client settings object
 * @returns {string} The field type ('boolean', 'emails', 'array', 'json', 'string')
 */
const getFieldType = (fieldName, value, clientSettings) => {
  if (!clientSettings || clientSettings[fieldName] === undefined) {
    return "string";
  }
  
  const originalValue = clientSettings[fieldName];
  
  // Check if the value has description/value structure
  if (originalValue && typeof originalValue === "object" && originalValue.description && originalValue.value !== undefined) {
    const actualValue = originalValue.value;
    
    if (typeof actualValue === "boolean") {
      return "boolean";
    }
    
    if (Array.isArray(actualValue)) {
      if (fieldName === "fromEmails") {
        return "emails";
      }
      return "array";
    }
    
    if (typeof actualValue === "object") {
      return "json";
    }
    
    return "string";
  }
  
  // Handle regular values
  if (typeof originalValue === "boolean") {
    return "boolean";
  }
  
  if (Array.isArray(originalValue)) {
    if (fieldName === "fromEmails") {
      return "emails";
    }
    return "array";
  }
  
  if (typeof originalValue === "object") {
    return "json";
  }
  
  return "string";
};

const useStyles = makeStyles({
  borderMain: {
    marginLeft: "-10px",
    marginRight: "-10px",
    marginTop: "-1px",
  },
  tableTitleDiv: {
    display: "flex",
    paddingLeft: "15px",
    flexDirection: 'row',
  },
  tableTitle: {
    display: "flex",
  },
  backIcon: {
    marginTop: "20px",
    marginRight: theme.spacing(1),
  },
  tableHeadRight: {
    float: "right",
    display: "flex",
    justifyContent: "flex-end",
    width: "100%"
  },
  tableHead: {
    backgroundColor: "9E9E9E"
  },
  root: {
    width: "100%",
    background: "white"
  },
  paper_root1: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-evenly'
  },
  paper_root2: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-evenly'
  },
  box: {
    display: "block",
    borderTop: "5px solid #173a64",
    padding: "20px 20px 15px 10px",
    marginLeft: '15px',
    marginRight: '15px',
    marginBottom: '3rem',
    wordBreak: "break-all",
  },
  header1: {
  },
  headerRight: {
    float: "right",
    display: "flex",
    paddingRight: "30px"
  },
  dialogBoxDetailDiv: {
    display: 'flex',
    justifyContent: 'space-evenly'
  },
  dialogDispatcherDetailDiv: {
    display: 'block',
    maxWidth: '40rem',
    minWidth: '40rem'
  },
  dialogTitleX: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
  },
  dialogButtonX: {
    padding: '0px',
  },
  submitButtonDiv: {
    minHeight: "5vh",
    paddingLeft: "1rem",
    minWidth: '7vw'
  },
  formControl: {
    paddingRight: "0.5rem",
    minWidth: "21rem",
    maxWidth: "18rem",
    marginTop: theme.spacing(3),
    paddingBottom: '0px'
  },
  formControlChannel: {
    paddingRight: "0.5rem",
    minWidth: "24rem",
    maxWidth: "20rem",
    marginTop: theme.spacing(3),
    paddingBottom: '0px',
    variant: "outlined"
  },
  formControlChannelInputLabel: {
    paddingLeft: "1rem"
  },
  smallTextIcon: {
  },
  inputBox: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(2),
    width: theme.spacing(20),
    marginRight: theme.spacing(3),
    height: theme.spacing(4.5),
    borderColor: 'black',
  },
  errorText: {
    display: 'flex',
    float: "left",
    padding: theme.spacing(1),
    color: 'red',
    fontWeight: 'bold',
    paddingBottom: '2px',
  },
  tablePaginationDiv: {
    marginTop: "-0.75rem",
    width: "100%",
    justifyContent: "flex-end",
    marginRight: theme.spacing(1),
  },
  labelRoot: {
    fontSize: 15,
    fontWeight: "bold",
    color: "black",
    "&$labelFocused": {
      color: "black",
      fontWeight: "bold",
      fontSize: 16,
    }
  },
  tableHeadRow: {
    fontWeight: "700"
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1
  },
  labelFocused: {},
  datePickerDiv: {
  },
  datePicker: {
    justifyContent: "flex-end",
  },
  channelSelectDropdown: {
    height: theme.spacing(7)
  },
  ackIdtext: {
    marginTop: '0'
  },
  filterButtonDiv: {
    display: 'flex',
    float: "right",
    marginRight: '1rem',
    marginTop: '1rem',
    overflow: 'auto'
  },
  transactionListDiv: {
    display: "block",
    borderTop: "5px solid #173a64",
    padding: "20px 20px 60px 10px",
    marginLeft: '15px',
    marginRight: '15px',
    marginBottom: '1.5rem',
    wordBreak: "break-all",
  },
  tableBorder: {
    border: "0.8px solid #00000021"
  },
  tableContainer: {
    padding: "20px",
    paddingTop: "0px"
  },
  noRecordsRow: {
    background: "#f8f8f8"
  },
  tableWrapper: {
    width: "100%",
    maxHeight: "70vh",
    overflow: "auto",
    fontFamily: "courier"
  },
  dialogButton: {
    fontWeight: "700",
    fontFamily: "sans-serif"
  },
  formControl1: {
    paddingRight: "2.5rem",
    minWidth: 290,
    maxWidth: 290,
    marginTop: '2px'
  },
  filterButtonDiv1: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  groupByContainer: {
    flex: 1,
    marginRight: '10px',
  },
  buttonContainer: {
    display: 'flex',
    gap: '10px',
  },
  submitFilterButton: {
    marginRight: "2rem",
    float: "right",
  },
  horizontalLine: {
    height: "4px",
    border: "none",
    boxShadow: "none",
    backgroundColor: "#36454F",
    margin: theme.spacing(2, 0),
  },
  tableProp: {
    minWidth: 650,
  },
  checkBox: {
    marginBottom: theme.spacing(1),
  },
});

/**
 * ClientDetails Component
 * 
 * Provides a comprehensive interface for viewing and managing client details including:
 * - Basic client information display
 * - API key management (create, activate, deactivate)
 * - Feature configuration management
 * - Custom field addition and management
 * - Client secret regeneration
 * 
 * @returns {JSX.Element} The ClientDetails component
 */
export default function ClientDetails() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const client = location.state && location.state.client ? location.state.client : null;
  // UI State
  const [backdrop, setBackdrop] = useState(false);
  const [showRegeneratedTable, setShowRegeneratedTable] = useState(false);
  const [showAllApiKeys, setShowAllApiKeys] = useState(false);
  const [confirmUpdateDialog, setConfirmUpdateDialog] = useState(false);
  
  // Client Data State
  const [clientData, setClientData] = useState({});
  const [fullClientData, setFullClientData] = useState(null);
  const [regeneratedData, setRegeneratedData] = useState({});
  
  // Feature Configuration State
  const [selectedFeature, setSelectedFeature] = useState("");
  const [allFeatureData, setAllFeatureData] = useState({});
  const [originalFeatureData, setOriginalFeatureData] = useState({});
  const [dynamicFeatures, setDynamicFeatures] = useState([]);
  const [modifiedFeatures, setModifiedFeatures] = useState(new Set());
  
  // Custom Fields State
  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldValue, setNewFieldValue] = useState("");
  const [customFieldType, setCustomFieldType] = useState("string");
  const [addedCustomFields, setAddedCustomFields] = useState([]);
  
  // Validation State
  const [featureValidationErrors, setFeatureValidationErrors] = useState({});
  const [emailValidationError, setEmailValidationError] = useState(null);

  // ============================================================================
  // API FUNCTIONS
  // ============================================================================
  
  /**
   * Fetches API keys for the current client
   * Updates the fullClientData with fresh API key information
   */
  const fetchApiKeys = async () => {
    if (!client || !client.clientName) return;

    try {
      const response = await AdminDashboardService.getAllApiKeysForClient(client.clientName);
      const apiKeysData = response.data || [];
      
      // Update fullClientData with the fresh API key data
      setFullClientData((prevData) => ({
        ...prevData,
        clientApiKey: apiKeysData,
      }));
    } catch (error) {
      console.error("Error fetching API keys:", error);
      notify({
        type: "error",
        message:
          "Error fetching API keys: " +
          (error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : error.message),
      });
    }
  };

  /**
   * Fetches complete client details from the API
   * Initializes client data and fetches API keys
   */
  const fetchClientDetails = async () => {
    if (!client || !client.clientName) return;

    try {
      setBackdrop(true);
      const response = await AdminDashboardService.viewClients();
      const clientsData = response.data || [];
      const foundClient = clientsData.find(
        (c) => c.clientName === client.clientName
      );

      if (foundClient) {
        setFullClientData(foundClient);
        initializeClientData(foundClient);
        // Fetch detailed API key data after setting client data
        await fetchApiKeys();
      } else {
        notify({
          type: "error",
          message: "Client not found",
        });
      }
    } catch (error) {
      notify({
        type: "error",
        message:
          "Error fetching client details: " +
          (error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : error.message),
      });
    } finally {
      setBackdrop(false);
    }
  };
  /**
   * Initializes client data and sets up dynamic features
   * Processes client settings and creates feature options
   * 
   * @param {Object} clientInfo - The client information object
   */
  const initializeClientData = (clientInfo) => {
    const clientSettings = clientInfo.clientSettings || {};
    setClientData({
      clientName: clientInfo.clientName || "",
      clientId: clientInfo.clientId || "",
      clientDescription: clientInfo.clientDescription || "",
    });

    // Get ALL fields from clientSettings - make everything dynamic
    // Filter out updatedBy and updatedAt fields from Feature Configuration display
    const existingFields = Object.keys(clientSettings).filter(field => field !== 'updatedBy' && field !== 'updatedAt');

    // Create dynamic feature options from ALL client settings
    const dynamicFeatureOptions = existingFields.map((field) => ({
      value: field,
      label: field, // Keep original field name format from backend
    }));

    setDynamicFeatures(dynamicFeatureOptions);

    if (!selectedFeature && dynamicFeatureOptions.length > 0) {
      setSelectedFeature(dynamicFeatureOptions[0].value);
    }

    // Initialize feature data and track original values - COMPLETELY DYNAMIC
    const initialAllFeatureData = {};
    const originalData = {};

    dynamicFeatureOptions.forEach((option) => {
      if (clientSettings[option.value] !== undefined) {
        const fieldValue = clientSettings[option.value];
        
        // Check if the value has description/value structure
        if (fieldValue && typeof fieldValue === "object" && fieldValue.description && fieldValue.value !== undefined) {
          const actualValue = fieldValue.value;
          
          if (Array.isArray(actualValue)) {
            // Handle arrays (like fromEmails)
            if (option.value === "fromEmails") {
              const emailString = actualValue.join(", ");
              initialAllFeatureData[option.value] = emailString;
              originalData[option.value] = emailString;
            } else {
              const jsonString = JSON.stringify(actualValue, null, 2);
              initialAllFeatureData[option.value] = jsonString;
              originalData[option.value] = jsonString;
            }
          } else if (typeof actualValue === "object") {
            const jsonString = JSON.stringify(actualValue, null, 2);
            initialAllFeatureData[option.value] = jsonString;
            originalData[option.value] = jsonString;
          } else {
            const stringValue = actualValue.toString();
            initialAllFeatureData[option.value] = stringValue;
            originalData[option.value] = stringValue;
          }
        } else {
          // Handle regular values
          if (Array.isArray(fieldValue)) {
            // Handle arrays (like fromEmails)
            if (option.value === "fromEmails") {
              const emailString = fieldValue.join(", ");
              initialAllFeatureData[option.value] = emailString;
              originalData[option.value] = emailString;
            } else {
              const jsonString = JSON.stringify(fieldValue, null, 2);
              initialAllFeatureData[option.value] = jsonString;
              originalData[option.value] = jsonString;
            }
          } else if (typeof fieldValue === "object") {
            const jsonString = JSON.stringify(fieldValue, null, 2);
            initialAllFeatureData[option.value] = jsonString;
            originalData[option.value] = jsonString;
          } else {
            const stringValue = fieldValue.toString();
            initialAllFeatureData[option.value] = stringValue;
            originalData[option.value] = stringValue;
          }
        }
      } else {
        initialAllFeatureData[option.value] = "";
        originalData[option.value] = "";
      }
    });

    setAllFeatureData(initialAllFeatureData);
    setOriginalFeatureData(originalData);
    setModifiedFeatures(new Set());
    setFeatureValidationErrors({});
    setEmailValidationError(null);

    // Clear added custom fields when reinitializing
    setAddedCustomFields([]);
    setNewFieldName("");
    setNewFieldValue("");
    setCustomFieldType("string");
  };

  /**
   * Toggles the active state of an API key
   * 
   * @param {string} apiKey - The API key to toggle
   * @param {boolean} isCurrentlyActive - Current active state
   */
  const handleApiKeyToggle = async (apiKey, isCurrentlyActive) => {
    try {
      setBackdrop(true);

      const requestBody = {
        apiKey: apiKey,
        setState: isCurrentlyActive ? "deactivate" : "activate",
      };

      await AdminDashboardService.updateApiKeyState(client.clientName, requestBody);

      // Refresh API keys data from the dedicated API
      await fetchApiKeys();

      notify({
        type: "success",
        message: `API Key ${
          isCurrentlyActive ? "deactivated" : "activated"
        } successfully`,
      });
    } catch (error) {
      notify({
        type: "error",
        message:
          `Error ${
            isCurrentlyActive ? "deactivating" : "activating"
          } API Key: ` +
          (error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : error.message),
      });
    } finally {
      setBackdrop(false);
    }
  };

  /**
   * Gets API keys to display based on showAllApiKeys state
   * Sorts keys by active status and creation date
   * 
   * @param {Array} apiKeys - Array of API key objects
   * @returns {Array} Filtered and sorted API keys
   */
  const getDisplayApiKeys = (apiKeys) => {
    if (!Array.isArray(apiKeys)) return [];

    const sortedKeys = [...apiKeys]
      .sort((a, b) => {
        // First priority: active status (true comes first)
        if (a.isActive !== b.isActive) {
          return b.isActive - a.isActive;
        }
        // Second priority: latest date (newest first)
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });

    return showAllApiKeys ? sortedKeys : sortedKeys.slice(0, 3);
  };

  /**
   * Creates a new API key for the client
   * Handles maximum API key limit errors
   */
  const handleCreateApiKey = async () => {
    try {
      setBackdrop(true);
      const response = await AdminDashboardService.createApiKey(client.clientName);

      // Refresh API keys data from the dedicated API
      await fetchApiKeys();
      
      notify({
        type: "success",
        message: "API Key created successfully",
      });
    } catch (error) {
      // Handle specific error cases
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        // Check for maximum API keys reached error
        if (
          errorData.errorCode === "40910" ||
          (errorData.message &&
            errorData.message.includes("maximum number of active API keys"))
        ) {
          notify({
            type: "warning",
            message:
              "Maximum number of active API keys reached. Please deactivate an existing API key before creating a new one.",
          });
        } else {
          notify({
            type: "error",
            message:
              "Error creating API Key: " + (errorData.message || error.message),
          });
        }
      } else {
        notify({
          type: "error",
          message: "Error creating API Key: " + error.message,
        });
      }
    } finally {
      setBackdrop(false);
    }
  };
  /**
   * Toggles the visibility of the regenerated secrets table
   */
  const toggleRegeneratedTable = () => {
    setShowRegeneratedTable(!showRegeneratedTable);
  };

  /**
   * Toggles the custom field type and clears the value
   * 
   * @param {string} type - The field type to switch to
   */
  const handleCustomFieldTypeToggle = (type) => {
    if (customFieldType === type) {
      return;
    } else {
      setCustomFieldType(type);
      setNewFieldValue("");
    }
  };

  const validateFieldName = (fieldName) => {
    // Field name should be a valid string: alphanumeric, camelCase, no spaces or special chars except underscore
    const fieldNameRegex = /^[a-zA-Z][a-zA-Z0-9_]*$/;

    if (!fieldName || fieldName.trim() === "") {
      return { isValid: false, error: "Field name cannot be empty" };
    }

    if (!fieldNameRegex.test(fieldName)) {
      return {
        isValid: false,
        error:
          "Field name must start with a letter and contain only letters, numbers, and underscores",
      };
    }

    return { isValid: true, error: null };
  };

  const validateJSON = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);

      // Additional validation for JSON keys
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        !Array.isArray(parsed)
      ) {
        for (const key in parsed) {
          if (typeof key !== "string" || key.trim() === "") {
            return {
              isValid: false,
              error: "All JSON keys must be non-empty strings",
            };
          }
          // Optionally validate key format (similar to field names)
          const keyRegex = /^[a-zA-Z][a-zA-Z0-9_]*$/;
          if (!keyRegex.test(key)) {
            return {
              isValid: false,
              error: `JSON key '${key}' must start with a letter and contain only letters, numbers, and underscores`,
            };
          }
        }
      }

      return { isValid: true, error: null };
    } catch (error) {
      return { isValid: false, error: error.message };
    }
  };

  const validateEmails = (emailString) => {
    if (!emailString || emailString.trim() === "") {
      return { isValid: true, error: null }; // Empty values are allowed
    }

    const emails = emailString
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email);
    const emailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

    for (const email of emails) {
      if (!emailRegex.test(email)) {
        return {
          isValid: false,
          error: `Invalid email format: ${email}`,
        };
      }
    }

    return { isValid: true, error: null };
  };

  const validateFeatureValue = (value) => {
    if (!value || value.trim() === "") {
      return { isValid: true, error: null }; // Empty values are allowed
    }

    const trimmedValue = value.trim();

    // Check if it's meant to be a boolean
    if (trimmedValue === "true" || trimmedValue === "false") {
      return { isValid: true, error: null, type: "boolean" };
    }

    // Check if it looks like it should be a boolean but is invalid
    if (
      trimmedValue.toLowerCase() === "true" ||
      trimmedValue.toLowerCase() === "false"
    ) {
      return {
        isValid: false,
        error: `Boolean values must be exactly 'true' or 'false' (case-sensitive), not '${trimmedValue}'`,
      };
    }

    // Check if it's JSON (starts with { or [)
    if (trimmedValue.startsWith("{") || trimmedValue.startsWith("[")) {
      const jsonValidation = validateJSON(trimmedValue);
      return {
        isValid: jsonValidation.isValid,
        error: jsonValidation.error,
        type: "json",
      };
    }

    // If it's a plain string, it's valid
    return { isValid: true, error: null, type: "string" };
  };

  const handleRegenerate = async () => {
    try {
      setBackdrop(true);
      const response = await AdminDashboardService.generateClientCredentials(
        client.clientName,
        true
      );

      const responseWithClientName = {
        clientName: client.clientName,
        ...response.data,
      };
      setRegeneratedData(responseWithClientName);
      setShowRegeneratedTable(true);

      notify({
        type: "success",
        message: "Client secrets regenerated successfully",
      });
    } catch (error) {
      setShowRegeneratedTable(false);
      notify({
        type: "error",
        message:
          "Error regenerating client secrets: " +
          (error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : error.message),
      });
    } finally {
      setBackdrop(false);
    }
  };

  const copyRegeneratedData = () => {
    try {
      let copyText = "Regenerated Client Secret:\n\n";
      Object.keys(regeneratedData).forEach((key) => {
        if (key === "clientEmails" && Array.isArray(regeneratedData[key])) {
          copyText += `${key}: ${regeneratedData[key].join(", ")}\n`;
        } else {
          copyText += `${key}: ${regeneratedData[key]}\n`;
        }
      });

      navigator.clipboard
        .writeText(copyText)
        .then(() => {
          notify({
            type: "success",
            message: "Regenerated client secret copied to clipboard",
          });
        })
        .catch(() => {
          const textArea = document.createElement("textarea");
          textArea.value = copyText;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand("copy");
          document.body.removeChild(textArea);

          notify({
            type: "success",
            message: "Regenerated client secret copied to clipboard",
          });
        });
    } catch (error) {
      notify({
        type: "error",
        message: "Failed to copy regenerated client secret",
      });
    }
  };
  /**
   * Effect to fetch client details when component mounts or client changes
   */
  useEffect(() => {
    if (client) {
      fetchClientDetails();
    }
  }, [client]);

  /**
   * Handles navigation back to the previous page
   */
  const handleBack = () => {
    history.goBack();
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setClientData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFeatureToggle = (featureValue) => {
    if (selectedFeature === featureValue) {
      setSelectedFeature("");
    } else {
      setSelectedFeature(featureValue);
      // Feature selected
    }
  };

  const handleFeatureDataChange = (event) => {
    if (selectedFeature) {
      const newValue = event.target.value;

      // Always allow the change to be made
      setAllFeatureData((prev) => ({
        ...prev,
        [selectedFeature]: newValue,
      }));

      // Validate based on field type
      let validation = { isValid: true, error: null };
      
      if (selectedFeature === "fromEmails") {
        validation = validateEmails(newValue);
      } else {
        validation = validateFeatureValue(newValue);
      }
      
      setFeatureValidationErrors((prev) => ({
        ...prev,
        [selectedFeature]: validation.isValid ? null : validation.error,
      }));

              // Track if this feature has been modified
        const isModified = newValue !== originalFeatureData[selectedFeature];
        setModifiedFeatures((prev) => {
          const newSet = new Set(prev);
          if (isModified) {
            newSet.add(selectedFeature);
          } else {
            newSet.delete(selectedFeature);
          }
          
          return newSet;
        });
    }
  };

  const confirmUpdate = () => {
    setConfirmUpdateDialog(true);
  };

  const handleUpdate = async () => {
    setConfirmUpdateDialog(false);
    try {
      setBackdrop(true);

      // Check for validation errors before proceeding
      const hasValidationErrors = Object.values(featureValidationErrors).some(
        (error) => error !== null
      );
      if (hasValidationErrors) {
        const errorFeatures = Object.keys(featureValidationErrors).filter(
          (key) => featureValidationErrors[key] !== null
        );
        notify({
          type: "error",
          message: `Please fix validation errors in: ${errorFeatures.join(
            ", "
          )}`,
        });
        setBackdrop(false);
        return;
      }

      // Check for email validation errors
      if (emailValidationError) {
        notify({
          type: "error",
          message: `Please fix email validation error: ${emailValidationError}`,
        });
        setBackdrop(false);
        return;
      }

      // Build the complete clientSettings object from all feature data
      const clientSettings = {};

      console.log("=== PROCESSING ALL FEATURE DATA ===");
      
      dynamicFeatures.forEach((feature) => {
        const featureValue = allFeatureData[feature.value];
        const fieldType = getFieldType(feature.value, featureValue, fullClientData && fullClientData.clientSettings);
        
        console.log(`Processing feature: ${feature.value}, value: ${featureValue}, type: ${fieldType}`);

        if (featureValue !== undefined && featureValue !== "") {
          // Check if original value has description/value structure
          const originalValue = fullClientData && fullClientData.clientSettings && fullClientData.clientSettings[feature.value];
          const hasDescriptionStructure = originalValue && typeof originalValue === "object" && originalValue.description && originalValue.value !== undefined;
          
          if (fieldType === "boolean") {
            const booleanValue = featureValue === "true";
            if (hasDescriptionStructure) {
              // Preserve the description/value structure
              clientSettings[feature.value] = {
                description: originalValue.description,
                value: booleanValue
              };
              console.log(`Processed as boolean with structure: ${feature.value} =`, clientSettings[feature.value]);
            } else {
              clientSettings[feature.value] = booleanValue;
              console.log(`Processed as boolean: ${feature.value} = ${booleanValue}`);
            }
          } else if (fieldType === "emails" || feature.value === "fromEmails") {
            // Handle fromEmails as array
            const emailArray = featureValue
              .split(",")
              .map((email) => email.trim())
              .filter((email) => email);
            if (hasDescriptionStructure) {
              clientSettings[feature.value] = {
                description: originalValue.description,
                value: emailArray
              };
              console.log(`Processed as email array with structure: ${feature.value} =`, clientSettings[feature.value]);
            } else {
              clientSettings[feature.value] = emailArray;
              console.log(`Processed as email array: ${feature.value} =`, emailArray);
            }
          } else if (fieldType === "json" || fieldType === "array") {
            try {
              const parsedValue = JSON.parse(featureValue);
              if (hasDescriptionStructure) {
                clientSettings[feature.value] = {
                  description: originalValue.description,
                  value: parsedValue
                };
                console.log(`Processed as JSON with structure: ${feature.value} =`, clientSettings[feature.value]);
              } else {
                clientSettings[feature.value] = parsedValue;
                console.log(`Processed as JSON: ${feature.value} =`, parsedValue);
              }
            } catch (e) {
              if (hasDescriptionStructure) {
                clientSettings[feature.value] = {
                  description: originalValue.description,
                  value: featureValue
                };
                console.log(`JSON parse failed, kept as string with structure: ${feature.value} =`, clientSettings[feature.value]);
              } else {
                clientSettings[feature.value] = featureValue;
                console.log(`JSON parse failed, kept as string: ${feature.value} = ${featureValue}`);
              }
            }
          } else {
            if (hasDescriptionStructure) {
              clientSettings[feature.value] = {
                description: originalValue.description,
                value: featureValue
              };
              console.log(`Processed as string with structure: ${feature.value} =`, clientSettings[feature.value]);
            } else {
              clientSettings[feature.value] = featureValue;
              console.log(`Processed as string: ${feature.value} = ${featureValue}`);
            }
          }
        } else if (featureValue === "") {
          // Handle empty values based on original type
          const originalValue = fullClientData && fullClientData.clientSettings && fullClientData.clientSettings[feature.value];
          
          // Check if original value has description/value structure
          if (originalValue && typeof originalValue === "object" && originalValue.description && originalValue.value !== undefined) {
            const actualOriginalValue = originalValue.value;
            if (typeof actualOriginalValue === "boolean") {
              clientSettings[feature.value] = {
                description: originalValue.description,
                value: false
              };
            } else if (Array.isArray(actualOriginalValue)) {
              clientSettings[feature.value] = {
                description: originalValue.description,
                value: []
              };
            } else {
              clientSettings[feature.value] = {
                description: originalValue.description,
                value: ""
              };
            }
          } else {
            if (typeof originalValue === "boolean") {
              clientSettings[feature.value] = false;
            } else if (Array.isArray(originalValue)) {
              clientSettings[feature.value] = [];
            } else {
              clientSettings[feature.value] = "";
            }
          }
        }
      });

      addedCustomFields.forEach((field) => {
        console.log(
          `Processing custom field: ${field.name}, type: ${field.type}, value: ${field.value}`
        );

        try {
          if (field.type === "json") {
            const parsedValue = JSON.parse(field.value);
            clientSettings[field.name] = parsedValue;
            console.log(
              `Custom field parsed as JSON: ${field.name} =`,
              parsedValue
            );
          } else if (field.type === "boolean") {
            clientSettings[field.name] = field.value === "true";
            console.log(
              `Custom field parsed as boolean: ${field.name} = ${
                field.value === "true"
              }`
            );
          } else {
            clientSettings[field.name] = field.value;
            console.log(
              `Custom field kept as string: ${field.name} = ${field.value}`
            );
          }
        } catch (e) {
          clientSettings[field.name] = field.value;
          console.log(
            `Custom field parsing failed, kept as string: ${field.name} = ${field.value}`
          );
        }
      });

      // Pass clientSettings directly (not wrapped in another object)
      await AdminDashboardService.updateClient(client.clientName, clientSettings);

      notify({
        type: "success",
        message: "Client updated successfully",
      });

      // Update completed successfully

      // Refresh data with listClient and repopulate everything
      await fetchClientDetails();
    } catch (error) {
      console.error("=== UPDATE ERROR ===");
      console.error(error);
      notify({
        type: "error",
        message:
          "Error updating client: " +
          (error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : error.message),
      });
    } finally {
      setBackdrop(false);
    }
  };

  const clearFields = () => {
    if (fullClientData) {
      initializeClientData(fullClientData);
    }
  };

  // Generate consolidated changes JSON - only changed fields
  const getConsolidatedChanges = () => {
    const clientSettings = {};

    // Only include modified features
    modifiedFeatures.forEach((featureName) => {
      const featureValue = allFeatureData[featureName];
      const fieldType = getFieldType(featureName, featureValue, fullClientData && fullClientData.clientSettings);
      
      // Check if original value has description/value structure
      const originalValue = fullClientData && fullClientData.clientSettings && fullClientData.clientSettings[featureName];
      const hasDescriptionStructure = originalValue && typeof originalValue === "object" && originalValue.description && originalValue.value !== undefined;
      
      if (featureValue !== undefined && featureValue !== "") {
        if (fieldType === "boolean") {
          const booleanValue = featureValue === "true";
          if (hasDescriptionStructure) {
            clientSettings[featureName] = {
              description: originalValue.description,
              value: booleanValue
            };
          } else {
            clientSettings[featureName] = booleanValue;
          }
        } else if (fieldType === "emails" || featureName === "fromEmails") {
          const emailArray = featureValue
            .split(",")
            .map((email) => email.trim())
            .filter((email) => email);
          if (hasDescriptionStructure) {
            clientSettings[featureName] = {
              description: originalValue.description,
              value: emailArray
            };
          } else {
            clientSettings[featureName] = emailArray;
          }
        } else if (fieldType === "json" || fieldType === "array") {
          try {
            const parsedValue = JSON.parse(featureValue);
            if (hasDescriptionStructure) {
              clientSettings[featureName] = {
                description: originalValue.description,
                value: parsedValue
              };
            } else {
              clientSettings[featureName] = parsedValue;
            }
          } catch (e) {
            if (hasDescriptionStructure) {
              clientSettings[featureName] = {
                description: originalValue.description,
                value: featureValue
              };
            } else {
              clientSettings[featureName] = featureValue;
            }
          }
        } else {
          if (hasDescriptionStructure) {
            clientSettings[featureName] = {
              description: originalValue.description,
              value: featureValue
            };
          } else {
            clientSettings[featureName] = featureValue;
          }
        }
      } else if (featureValue === "") {
        if (hasDescriptionStructure) {
          const actualOriginalValue = originalValue.value;
          if (typeof actualOriginalValue === "boolean") {
            clientSettings[featureName] = {
              description: originalValue.description,
              value: false
            };
          } else if (Array.isArray(actualOriginalValue)) {
            clientSettings[featureName] = {
              description: originalValue.description,
              value: []
            };
          } else {
            clientSettings[featureName] = {
              description: originalValue.description,
              value: ""
            };
          }
        } else {
          if (typeof originalValue === "boolean") {
            clientSettings[featureName] = false;
          } else if (Array.isArray(originalValue)) {
            clientSettings[featureName] = [];
          } else {
            clientSettings[featureName] = "";
          }
        }
      }
    });

    // Add only new custom fields
    addedCustomFields.forEach((field) => {
      try {
        if (field.type === "json") {
          const parsedValue = JSON.parse(field.value);
          clientSettings[field.name] = parsedValue;
        } else if (field.type === "boolean") {
          clientSettings[field.name] = field.value === "true";
        } else {
          clientSettings[field.name] = field.value;
        }
      } catch (e) {
        clientSettings[field.name] = field.value;
      }
    });

    return clientSettings;
  };

  if (!client) {
    return (
      <Paper className={classes.root}>
        <Typography variant="h6">No client data found</Typography>
        <Button onClick={handleBack} startIcon={<ArrowBackIcon />}>
          Go Back
        </Button>
      </Paper>
    );
  }

  return (
    <div className={classes.borderMain}>
      <Backdrop open={backdrop} style={{ color: "primary", zIndex: 1000000 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Paper className={classes.root}>
        <Grid item xs={12} style={{ minHeight: "87vh" }}>
          {/* Header with Regenerate Button */}
          <div
            className={classes.tableTitleDiv}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingTop: "20px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <ArrowBackIcon
                size="medium"
                className={classes.backIcon}
                onClick={handleBack}
                style={{ marginTop: "0px" }}
              />
              <h2 style={{ margin: 0, marginRight: "12px" }}>Client Details - </h2>
              <div
                style={{
                  backgroundColor: "#f0f0f0",
                  padding: "6px 12px",
                  borderRadius: "16px",
                  border: "1px solid #d0d0d0",
                  fontWeight: "bold",
                  fontSize: "16px",
                  color: "#333",
                }}
              >
                {clientData.clientName}
              </div>
            </div>
            <Button
              color="primary"
              variant="contained"
              className={classes.submitFilterButton}
              onClick={handleRegenerate}
              startIcon={<RefreshIcon />}
              style={{
                minWidth: "180px",
                padding: "8px 32px",
                marginRight: "20px",
              }}
            >
              Regenerate Client Secret
            </Button>
          </div>

          {/* Regenerated Secrets Section */}
          <Collapse in={showRegeneratedTable}>
            <Fragment>
              <div
                style={{
                  padding: "20px",
                  margin: "16px 0",
                  backgroundColor: "#f8f8f8",
                  borderRadius: "4px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                }}
              >
                <div
                  className={classes.tableTitleDiv}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "-10px",
                    marginLeft: "0px",
                    paddingLeft: "0px"
                  }}
                >
                  <div className={classes.tableTitle}>
                    <h2 style={{ marginLeft: "20px" }}>{"Regenerated Client Secret"}</h2>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginRight: "20px",
                    }}
                  >
                    <Tooltip
                      title="Copy regenerated secrets to clipboard"
                      placement="top"
                    >
                      <IconButton
                        onClick={copyRegeneratedData}
                        color="primary"
                        style={{ marginRight: "8px" }}
                      >
                        <FileCopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Hide regenerated secrets" placement="top">
                      <IconButton
                        onClick={toggleRegeneratedTable}
                        color="default"
                      >
                        <ExpandLessIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
                
                <div className={classes.box}>
                <Table
                  className={classes.tableProp}
                  stickyHeader
                  aria-label="sticky table"
                >
                  <TableHead>
                    <TableRow>
                      {RESPONSE_COLUMNS.map((column) => (
                        <TableCell
                          width={column.width}
                          key={column.id}
                          align="center"
                          className={classes.tableHeadRow}
                        >
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.keys(regeneratedData)
                      .filter((key) => regeneratedData.hasOwnProperty(key))
                      .map((key, index) => (
                        <TableRow
                          hover
                          role="data-row"
                          tabIndex={-1}
                          key={index}
                        >
                          <TableCell align="center">
                            <strong>{key}</strong>
                          </TableCell>
                          <TableCell align="center">
                            {key === "clientEmails" &&
                            Array.isArray(regeneratedData[key])
                              ? regeneratedData[key].join(", ")
                              : regeneratedData[key]}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            </Fragment>
          </Collapse>

          <div style={{ padding: "24px 0" }}>
            <div className={classes.box}>
              {/* Basic Information Section */}
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  marginBottom: "16px",
                  width: "100%",
                }}
              >
                <FormControl style={{ flex: 0.3 }}>
                  <TextField
                    label="Client Name"
                    variant="outlined"
                    value={clientData.clientName || ""}
                    disabled={true}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </FormControl>
                <FormControl style={{ flex: 0.3 }}>
                  <TextField
                    label="Client ID"
                    variant="outlined"
                    value={clientData.clientId || ""}
                    disabled={true}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </FormControl>
                <FormControl style={{ flex: 1 }}>
                  <TextField
                    label="Description"
                    variant="outlined"
                    value={clientData.clientDescription || ""}
                    disabled={true}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </FormControl>
              </div>



              {/* API Key Management Section */}
              {fullClientData &&
                Array.isArray(fullClientData.clientApiKey) &&
                fullClientData.clientApiKey.length > 0 && (
                  <div style={{ marginBottom: "16px" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "12px",
                        marginLeft: "20px",
                      }}
                    >
                      <Typography variant="h6" style={{ fontWeight: "bold" }}>
                        API Key Management
                      </Typography>
                      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <Button
                          color="primary"
                          variant="contained"
                          onClick={handleCreateApiKey}
                          startIcon={<AddIcon />}
                          style={{
                            minWidth: "140px",
                            padding: "6px 16px",
                          }}
                        >
                          Create API Key
                        </Button>
                        <Button
                          color="default"
                          variant="contained"
                          onClick={() => setShowAllApiKeys(!showAllApiKeys)}
                          endIcon={showAllApiKeys ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          style={{
                            marginRight: "20px",
                            padding: "6px 16px",
                          }}
                        >
                          {showAllApiKeys ? "Show Latest" : "Show All API'S"}
                        </Button>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        marginBottom: "16px",
                        width: "100%",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        {getDisplayApiKeys(fullClientData.clientApiKey).map(
                          (keyObj, index) => {
                            const apiKey = keyObj.apiKey || keyObj;
                            const isActive =
                              keyObj.isActive !== undefined
                                ? keyObj.isActive
                                : true;
                            const createdAt = keyObj.createdAt;
                            const updatedAt = keyObj.updatedAt;
                            const createdBy = keyObj.createdBy;
                            const updatedBy = keyObj.updatedBy;

                            return (
                            <div
                            key={index}
                            style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "12px",
                            backgroundColor: "#f9f9f9",
                            borderRadius: "8px",
                            marginBottom: "8px",
                              border: "1px solid #e0e0e0",
                              }}
                            >
                            <div style={{ flex: 1 }}>
                            <Typography
                            variant="body2"
                            style={{
                            fontFamily: "monospace",
                            fontSize: "0.9rem",
                              marginBottom: "4px",
                              }}
                            >
                            <strong>API Key {index + 1}:</strong>{" "}
                              {apiKey}
                            </Typography>
                            <div>
                            {createdAt && (
                            <Typography
                                variant="caption"
                              style={{ color: "#666", display: "block" }}
                            >
                                Created:{" "}
                                  {new Date(createdAt).toLocaleString()}
                                  </Typography>
                                     )}
                                     {updatedAt && (
                                       <Typography
                                         variant="caption"
                                         style={{ color: "#666", display: "block" }}
                                       >
                                         Updated:{" "}
                                         {new Date(updatedAt).toLocaleString()}
                                       </Typography>
                                     )}
                                     {createdBy && (
                                       <Typography
                                         variant="caption"
                                         style={{ color: "#666", display: "block" }}
                                       >
                                         Created By:{" "}
                                         {createdBy}
                                       </Typography>
                                     )}
                                     {updatedBy && (
                                       <Typography
                                         variant="caption"
                                         style={{ color: "#666", display: "block" }}
                                       >
                                         Updated By:{" "}
                                         {updatedBy}
                                       </Typography>
                                     )}
                                   </div>
                                 </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                  }}
                                >
                                  <Chip
                                    label={isActive ? "Active" : "Inactive"}
                                    color={isActive ? "primary" : "default"}
                                    size="small"
                                  />
                                  <Button
                                    variant="contained"
                                    color={isActive ? "default" : "primary"}
                                    size="small"
                                    onClick={() =>
                                      handleApiKeyToggle(apiKey, isActive)
                                    }
                                    style={{ minWidth: "100px" }}
                                  >
                                    {isActive ? "Deactivate" : "Activate"}
                                  </Button>
                                </div>
                              </div>
                            );
                          }
                        )}
                      </div>
                    </div>
                  </div>
                )}

              {/* Show Create API Key button even if no API keys exist */}
              {(!fullClientData ||
                !fullClientData.clientApiKey ||
                fullClientData.clientApiKey.length === 0) && (
                <div style={{ marginBottom: "16px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "12px",
                      marginLeft: "20px",
                    }}
                  >
                    <Typography variant="h6" style={{ fontWeight: "bold" }}>
                      API Key Management
                    </Typography>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      <Button
                        color="primary"
                        variant="contained"
                        onClick={handleCreateApiKey}
                        startIcon={<AddIcon />}
                        style={{
                          minWidth: "140px",
                          padding: "6px 16px",
                        }}
                      >
                        Create API Key
                      </Button>
                      <Button
                        color="default"
                        variant="contained"
                        onClick={() => setShowAllApiKeys(!showAllApiKeys)}
                        endIcon={showAllApiKeys ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        style={{
                          marginRight: "20px",
                          padding: "6px 16px",
                        }}
                      >
                        {showAllApiKeys ? "Show Latest 3" : "Show All API'S"}
                      </Button>
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "20px",
                      backgroundColor: "#f5f5f5",
                      borderRadius: "8px",
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      No API keys found. Click "Create API Key" to generate a
                      new one.
                    </Typography>
                  </div>
                </div>
              )}
            </div>

            <hr
              className={classes.horizontalLine}
              style={{
                height: "4px",
                border: "none",
                boxShadow: "none",
                backgroundColor: "#36454F",
              }}
            />
            {/* Feature Configuration Section */}
            <div style={{ marginTop: "20px" }}>
              <div
                style={{
                  padding: "20px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                }}
              >
                <div
                  className={classes.tableTitleDiv}
                  style={{ 
                    marginBottom: "16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                >
                  <h2>Feature Configuration</h2>
                </div>

                {dynamicFeatures.length > 0 ? (
                  <>
                    <div style={{ marginBottom: "16px" }}>
                      {dynamicFeatures
                        .sort((a, b) => a.label.localeCompare(b.label))
                        .map((option) => (
                        <FormControlLabel
                          key={option.value}
                          className={classes.checkBox}
                          control={
                            <Checkbox
                              checked={selectedFeature === option.value}
                              onChange={() => handleFeatureToggle(option.value)}
                              color="primary"
                            />
                          }
                          label={
                            <span
                              style={{ display: "flex", alignItems: "center" }}
                              onMouseEnter={(e) => {
                                const tooltip = document.createElement('div');
                                tooltip.id = `tooltip-${option.value}`;
                                
                                // Get description from featureDescriptions or from the field structure
                                let description = FEATURE_DESCRIPTIONS[option.value];
                                
                                if (!description && fullClientData && fullClientData.clientSettings) {
                                  const fieldValue = fullClientData.clientSettings[option.value];
                                  if (fieldValue && typeof fieldValue === "object" && fieldValue.description) {
                                    // Keep the original field name format from backend
                                    description = `${option.value} - ${fieldValue.description}`;
                                  }
                                }
                                
                                tooltip.innerHTML = description || "No description available";
                                
                                // Position tooltip relative to the button
                                const buttonRect = e.target.getBoundingClientRect();
                                const tooltipWidth = 400; // Fixed width
                                const tooltipHeight = 80; // Fixed height
                                
                                // Calculate position to prevent overflow
                                let leftPosition = buttonRect.left + 50;
                                const windowWidth = window.innerWidth;
                                
                                // If tooltip would overflow to the right, adjust left position
                                if (leftPosition + tooltipWidth > windowWidth - 20) {
                                  leftPosition = windowWidth - tooltipWidth - 20;
                                }
                                
                                // Ensure tooltip doesn't go off the left side
                                if (leftPosition < 20) {
                                  leftPosition = 20;
                                }
                                
                                tooltip.style.cssText = `
                                  position: fixed;
                                  top: ${buttonRect.top + 40}px;
                                  left: ${leftPosition}px;
                                  width: ${tooltipWidth}px;
                                  height: ${tooltipHeight}px;
                                  background: #333;
                                  color: white;
                                  padding: 12px 16px;
                                  border-radius: 8px;
                                  font-size: 14px;
                                  font-weight: 500;
                                  line-height: 1.5;
                                  z-index: 9999;
                                  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
                                  white-space: normal;
                                  word-wrap: break-word;
                                  text-align: left;
                                  overflow: auto;
                                `;
                                document.body.appendChild(tooltip);
                              }}
                              onMouseLeave={() => {
                                const tooltip = document.getElementById(`tooltip-${option.value}`);
                                if (tooltip) {
                                  tooltip.remove();
                                }
                              }}
                            >
                              {option.label}
                              {modifiedFeatures.has(option.value) && (
                                <CheckIcon
                                  style={{
                                    marginLeft: "8px",
                                    fontSize: "16px",
                                    color: "#000",
                                    backgroundColor: "#FFD700",
                                    borderRadius: "50%",
                                    padding: "2px",
                                    width: "20px",
                                    height: "20px",
                                  }}
                                />
                              )}
                            </span>
                          }
                        />
                      ))}
                        

                     </div>

                     <div
                       style={{
                         marginTop: "16px",
                         padding: "16px",
                         backgroundColor: "white",
                         borderRadius: "4px",
                       }}
                     >
                      {(() => {
                         
                         const selectedOption = dynamicFeatures.find(
                           (opt) => opt.value === selectedFeature
                         );
                         const featureLabel = selectedOption
                           ? selectedOption.label
                           : "Feature";
                         const validationError =
                           featureValidationErrors[selectedFeature];
                         return (
                           <>
                             <Typography
                               variant="subtitle1"
                               style={{
                                 marginBottom: "16px",
                                 fontWeight: "bold",
                               }}
                             >
                               Configure {featureLabel}:
                             </Typography>
                            {(() => {
                              const fieldType = getFieldType(selectedFeature, allFeatureData[selectedFeature], fullClientData && fullClientData.clientSettings);
                              
                              if (fieldType === "boolean") {
                                return (
                                  <TextField
                                    select
                                    label={`${featureLabel} Configuration`}
                                    variant="outlined"
                                    value={allFeatureData[selectedFeature] || "false"}
                                    onChange={handleFeatureDataChange}
                                    fullWidth
                                    InputLabelProps={{
                                      shrink: true,
                                    }}
                                    error={!!validationError}
                                  >
                                    {BOOLEAN_OPTIONS.map((option) => (
                                      <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                );
                              } else if (fieldType === "emails") {
                                return (
                                  <TextField
                                    label={`${featureLabel} Configuration`}
                                    variant="outlined"
                                    value={allFeatureData[selectedFeature] || ""}
                                    onChange={handleFeatureDataChange}
                                    fullWidth
                                    multiline
                                    rows={3}
                                    placeholder="email1@domain.com, email2@domain.com"
                                    InputLabelProps={{
                                      shrink: true,
                                    }}
                                    error={!!validationError}
                                    helperText={validationError || "Enter email addresses separated by commas"}
                                  />
                                );
                              } else {
                                return (
                                  <TextField
                                    label={`${featureLabel} Configuration`}
                                    variant="outlined"
                                    value={allFeatureData[selectedFeature] || ""}
                                    onChange={handleFeatureDataChange}
                                    fullWidth
                                    multiline
                                    rows={fieldType === "json" ? 15 : 8}
                                    placeholder={
                                      !allFeatureData[selectedFeature]
                                        ? fieldType === "json" 
                                          ? "Enter JSON object..." 
                                          : "Enter configuration value..."
                                        : ""
                                    }
                                    InputLabelProps={{
                                      shrink: true,
                                    }}
                                    error={!!validationError}
                                  />
                                );
                              }
                            })()}
                            {validationError && (
                              <Typography
                                variant="caption"
                                style={{
                                  color: "#f44336",
                                  marginTop: "8px",
                                  display: "block",
                                  fontSize: "0.75rem",
                                }}
                              >
                                ⚠️ {validationError}
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      padding: "20px",
                      backgroundColor: "white",
                      borderRadius: "4px",
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      No existing features found. Use the Custom Fields Dashboard to add new configurations.
                    </Typography>
                  </div>
                )}

                {/* Action Buttons */}
                {(modifiedFeatures.size > 0 || addedCustomFields.length > 0) && (
                  <div style={{
                    display: "flex",
                    gap: "12px",
                    justifyContent: "flex-end",
                    marginTop: "20px",
                    paddingTop: "16px"
                  }}>
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={confirmUpdate}
                      style={{
                        minWidth: "100px",
                        height: "40px"
                      }}
                    >
                      Review Changes
                    </Button>
                    <Button
                      color="primary"
                      variant="outlined"
                      onClick={clearFields}
                      style={{
                        minWidth: "100px",
                        height: "40px"
                      }}
                    >
                      Reset
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Grid>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmUpdateDialog}
        onClose={() => setConfirmUpdateDialog(false)}
        aria-labelledby="confirm-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="confirm-dialog-title">
          Confirm Update
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to update <strong>{clientData.clientName}</strong> client settings?
          </DialogContentText>
          <div style={{
            marginTop: "16px",
            padding: "12px",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
            fontFamily: "monospace",
            fontSize: "0.8rem",
            maxHeight: "200px",
            overflow: "auto"
          }}>
            <pre>{JSON.stringify(getConsolidatedChanges(), null, 2)}</pre>
          </div>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmUpdateDialog(false)} 
            color="default"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdate} 
            color="primary" 
            variant="contained"
          >
            Update changes in the client
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
